// SentryProvider.tsx
import React, { useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { fetchEnvironmentVar } from '../utils/miscUtils'

// Sentry DSN from environment variable
const SENTRY_DSN = fetchEnvironmentVar('REACT_APP_SENTRY_DSN')


interface SentryProviderProps {
  children: React.ReactNode
}

const SentryProvider: React.FC<SentryProviderProps> = ({ children }) => {
  useEffect(() => {
    // Only initialize Sentry if DSN is provided and in production
    if (SENTRY_DSN && process.env.NODE_ENV === 'production') {
      Sentry.init({
        dsn: SENTRY_DSN,
        integrations: [Sentry.browserTracingIntegration()],

        // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring
        // We recommend adjusting this value in production (0.1 - 0.5)
        tracesSampleRate: 0.2,

        // Capture environment
        environment: process.env.NODE_ENV,

        // Set maxBreadcrumbs (default is 100)
        maxBreadcrumbs: 50,

        // Configure beforeSend to sanitize error data if needed
        beforeSend (event) {
          // Example: Don't report errors from localhost
          if (window.location.hostname === 'localhost') {
            return null
          }
          return event
        }
      })
    }
  }, [])

  return <>{children}</>
}

// Create typed utility functions for error reporting
interface ErrorContext {
  [key: string]: any
}

const captureError = (error: Error, context: ErrorContext = {}): void => {
  if (SENTRY_DSN && process.env.NODE_ENV === 'production') {
    Sentry.captureException(error, {
      extra: context
    })
  } else {
    console.error('Error captured:', error, context)
  }
}

const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info'): void => {
  if (SENTRY_DSN && process.env.NODE_ENV === 'production') {
    Sentry.captureMessage(message, level)
  } else {
    console.log(`${level} message captured:`, message)
  }
}

export { captureError, captureMessage }
export default SentryProvider
